import breakpointTokens from './breakpoints.json'

export const BREAKPOINTS = Object.freeze({ ...breakpointTokens })

export const BREAKPOINT_NAMES = Object.freeze({
  SMALL_MOBILE: 'smallMobile',
  LARGE_MOBILE: 'largeMobile',
  TABLET: 'tablet',
  SMALL_DESKTOP: 'smallDesktop',
  DESKTOP: 'desktop',
  LARGE_DESKTOP: 'largeDesktop'
})

const widthBelow = (breakpoint) => `(width < ${BREAKPOINTS[breakpoint]})`

const widthAtOrAbove = (breakpoint) => `(width >= ${BREAKPOINTS[breakpoint]})`

const widthBetween = (minimum, maximum) =>
  `(${BREAKPOINTS[minimum]} <= width < ${BREAKPOINTS[maximum]})`

export const BREAKPOINT_QUERIES = Object.freeze({
  smallMobile: widthBelow('sm'),
  largeMobile: widthBetween('sm', 'md'),
  tablet: widthBetween('md', 'lg'),
  smallDesktop: widthBetween('lg', 'xl'),
  desktop: widthBetween('xl', '2xl'),
  largeDesktop: widthAtOrAbove('2xl'),
  largeMobileUp: widthAtOrAbove('sm'),
  tabletUp: widthAtOrAbove('md'),
  smallDesktopUp: widthAtOrAbove('lg'),
  desktopUp: widthAtOrAbove('xl'),
  largeDesktopUp: widthAtOrAbove('2xl'),
  mobileDown: widthBelow('md'),
  tabletDown: widthBelow('lg'),
  smallDesktopDown: widthBelow('xl'),
  desktopDown: widthBelow('2xl')
})

// Transitional exceptions live here so page styles never add numeric media
// queries. Delete each entry when its last legacy consumer is retired.
export const LEGACY_BREAKPOINT_QUERIES = Object.freeze({
  bannerSmallDesktopInclusiveDown: 'screen and (max-width: 1024px)',
  collectionDesktopUp: '(min-width: 1280px)',
  desktopUp: '(min-width: 1280px)',
  largeDesktopUp: '(min-width: 1440px)',
  mobileDown: '(max-width: 767px)',
  mobileInclusiveDown: '(max-width: 768px)',
  reactGridExtraExtraLargeUp: '(min-width: 1600px)',
  reactGridLargeUp: '(min-width: 992px)',
  reactGridSmallDown: '(max-width: 576px)',
  reactGridSmallUp: '(min-width: 576px)',
  releaseInformationCoverControlsDown: '(max-width: 1199.95px)',
  releaseInformationPromoWideUp: '(min-width: 1536px)',
  releaseInformationTabsUp: '(min-width: 1200px)',
  releasesActionButtonLargeMobile: '(min-width: 516px) and (max-width: 767px)',
  releasesUltraWideUp: '(min-width: 1920px)',
  searchDesktopUp: '(min-width: 1280px)',
  searchLargeDesktopUp: '(min-width: 1536px)',
  searchSmallDesktopUp: '(min-width: 1024px)',
  smallDesktop: '(min-width: 1024px) and (max-width: 1439px)',
  smallDesktopUp: '(min-width: 1024px)',
  tabletDown: '(max-width: 1023px)',
  tabletUp: '(min-width: 768px)'
})

// Legacy screen-class ranges used by useMediaQueries. Keep these exact while
// Statsig can still select the legacy responsive implementation.
export const LEGACY_MEDIA_QUERIES = Object.freeze({
  desktop: LEGACY_BREAKPOINT_QUERIES.largeDesktopUp,
  tabletAndAbove: LEGACY_BREAKPOINT_QUERIES.tabletUp,
  mobile: LEGACY_BREAKPOINT_QUERIES.mobileDown,
  tabletDown: LEGACY_BREAKPOINT_QUERIES.tabletDown,
  xlUp: LEGACY_BREAKPOINT_QUERIES.largeDesktopUp,
  lgDown: '(max-width: 1199.95px)',
  xlDown: '(max-width: 1439px)'
})

export const selectResponsiveBreakpointQuery = (
  queryName,
  { legacyQuery, useCanonical = false } = {}
) => {
  const canonicalQuery = BREAKPOINT_QUERIES[queryName]

  if (!canonicalQuery) {
    throw new Error(`Unknown responsive breakpoint query: ${queryName}`)
  }

  return useCanonical || !legacyQuery ? canonicalQuery : legacyQuery
}

export const createResponsiveBreakpointQuery =
  (queryName, { legacyQuery } = {}) =>
  ({ $responsiveBreakpointsV2 = false } = {}) =>
    selectResponsiveBreakpointQuery(queryName, {
      legacyQuery,
      useCanonical: $responsiveBreakpointsV2
    })
